import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type {
  NegociacaoAssinaturasState,
  PedidoDeParticipacoes,
  ConsultaAssinaturas,
} from '../types/negociacaoAssinaturasTypes';
import { carregarMaisParticipacoes } from './actions';

export const chaveDasParticipacoes = (
  pedido: Pick<PedidoDeParticipacoes, 'acordoId' | 'consulta'>,
): string => `${pedido.consulta.escopo}:${pedido.acordoId}`;

const contextoDaConsulta = (state: NegociacaoAssinaturasState, pedido: PedidoDeParticipacoes) =>
  pedido.consulta.escopo === 'lista'
    ? state.consultaVigente
    : state.detalhes[pedido.consulta.casoIds?.[0] ?? '']?.consultaVigente;

const chavesDoContexto = (
  state: NegociacaoAssinaturasState,
  consulta: Pick<ConsultaAssinaturas, 'escopo' | 'casoIds'>,
): string[] => {
  if (consulta.escopo === 'lista') {
    return Object.keys(state.participacoes).filter((chave) => chave.startsWith('lista:'));
  }
  const resultado = state.detalhes[consulta.casoIds?.[0] ?? '']?.resultado;
  return (
    resultado?.items.flatMap((item) =>
      item.documentos.map((documento) =>
        chaveDasParticipacoes({ acordoId: documento.acordoId, consulta }),
      ),
    ) ?? []
  );
};

/** A primeira página pode falhar: os cursores continuam pertencendo às pessoas ainda exibidas. */
export function invalidarConsultasDeParticipacoes(
  state: NegociacaoAssinaturasState,
  consulta: Pick<ConsultaAssinaturas, 'escopo' | 'casoIds'>,
): void {
  for (const chave of chavesDoContexto(state, consulta)) {
    const participacoes = state.participacoes[chave];
    if (!participacoes) continue;
    participacoes.carregando = false;
    participacoes.consultaVigente = null;
    participacoes.contextoVigente = null;
  }
}

export function limparParticipacoesDoContexto(
  state: NegociacaoAssinaturasState,
  consulta: Pick<ConsultaAssinaturas, 'escopo' | 'casoIds'>,
): void {
  for (const chave of chavesDoContexto(state, consulta)) delete state.participacoes[chave];
}

export function adicionarCasosDeParticipacoes(
  builder: ActionReducerMapBuilder<NegociacaoAssinaturasState>,
): void {
  builder
    .addCase(carregarMaisParticipacoes.pending, (state, action) => {
      state.participacoes[chaveDasParticipacoes(action.meta.arg)] = {
        pagina: state.participacoes[chaveDasParticipacoes(action.meta.arg)]?.pagina ?? 1,
        carregando: true,
        erro: null,
        consultaVigente: action.meta.requestId,
        contextoVigente: contextoDaConsulta(state, action.meta.arg) ?? null,
      };
    })
    .addCase(carregarMaisParticipacoes.fulfilled, (state, action) => {
      const pedido = action.meta.arg;
      const estado = state.participacoes[chaveDasParticipacoes(pedido)];
      if (
        !estado ||
        estado.consultaVigente !== action.meta.requestId ||
        estado.contextoVigente !== contextoDaConsulta(state, pedido)
      )
        return;
      const resultado =
        pedido.consulta.escopo === 'lista'
          ? state.resultado
          : state.detalhes[pedido.consulta.casoIds?.[0] ?? '']?.resultado;
      const documento = resultado?.items
        .flatMap((item) => item.documentos)
        .find((item) => item.acordoId === pedido.acordoId);
      if (!documento) return;
      const conhecidos = new Set(
        documento.participacoesEncontradas.map((item) => item.signatarioId),
      );
      documento.participacoesEncontradas.push(
        ...action.payload.items.filter((item) => !conhecidos.has(item.signatarioId)),
      );
      documento.participacoesTotal = action.payload.total;
      documento.participacoesHasMore = action.payload.hasNext;
      estado.carregando = false;
      estado.pagina = action.payload.page;
    })
    .addCase(carregarMaisParticipacoes.rejected, (state, action) => {
      const estado = state.participacoes[chaveDasParticipacoes(action.meta.arg)];
      if (!estado || estado.consultaVigente !== action.meta.requestId) return;
      estado.carregando = false;
      estado.erro = action.meta.aborted
        ? null
        : (action.payload ?? 'Não foi possível carregar mais pessoas');
    });
}
